import { useState } from "react";
import ProgressBar from "../progress-bar";
import CopyIcon from "../../../src/assests/CopyIcon.svg";
import { useChaseMenu } from "../../store/use-chase-menu";
import { toast } from "react-toastify";
import moment from "moment";
import CustomButton from "../custom-button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faPhone,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import { ParseErrors } from "../../utils/parse-errors";
import axiosInstance from "../../utils/axios-instance";
import { useNavigate } from "react-router-dom";
import downloadFile from "../../utils/download-file";

const ChaseDropMenu = () => {
  const navigate = useNavigate();
  const { initialValues } = useChaseMenu();
  const { timeline, logs: logsData, data } = initialValues;
  const [logs, setLogs] = useState({
    data: logsData,
    totalCount: logsData?.length,
  });
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const isSameCustomerChase = data?.isSameCustomerChase;

  // ---------------------------------------------- [ COPY TO CLIPBOARD ] --------------------------------------------------------------
  const CopytoClipboard = () => {
    navigator.clipboard.writeText(`${initialValues?.data?.paymentLink}`);
    toast.success("Copied");
  };

  // ---------------------------------------------- [ HANDLE TAKE AGENT PAYMENT ] --------------------------------------------------------------
  const HandleTakePayment = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/loan/create-agent-payment-token/${initialValues.data.policyReference}`,
      );

      window.open(`${res.data.link}`);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      toast.error(ParseErrors(error));
    }
  };

  const DownloadChaseDocument = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/loan/download/document/${initialValues.data.policyReference}`,
      );
      downloadFile(res.data.url);
      setLoading(false);
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
      setLoading(false);
    }
  };

  const loadMoreLogs = async () => {
    setIsFetching(true);
    try {
      const res = await axiosInstance.get(
        `/loan/find/logs/${initialValues.data.id}?limit=${limit}&offset=${
          limit + page
        }`,
      );
      setPage(page + limit);
      setLogs((prevState) => ({
        data: [...prevState.data, ...res.data.data],
        totalCount: res.data.totalCount,
      }));
      setIsFetching(false);
    } catch (error) {
      console.log(error);
      setIsFetching(false);
    }
  };

  return (
    <td colSpan={8}>
      <div className="bg-overlay border-y border-edge-faint px-5 py-5 animate-fade-in">
        <div className="w-full bg-surface rounded-2xl border border-edge p-5 sm:p-6">
          <div className="flex flex-col lg:flex-row items-start gap-6">
            <div className="w-full lg:flex-1 min-w-0">
              <div className="w-full flex items-center justify-between gap-4">
                <p className="flex items-center gap-2 text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                  Chase Timeline
                  {data?.QueuedCallId ? (
                    <FontAwesomeIcon
                      icon={faPhone}
                      title="Queued call"
                      className="text-[11px] text-text-secondary"
                    />
                  ) : null}
                </p>

                {initialValues?.data?.status === "Chargebacks" ? (
                  <CustomButton
                    disabled={loading}
                    className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-[0_8px_20px_-8px_rgba(16,185,129,0.65)] hover:-translate-y-px rounded-lg !h-9 !text-[13px] font-semibold cursor-pointer transition-all duration-200"
                    onClick={DownloadChaseDocument}
                  >
                    Download Document
                  </CustomButton>
                ) : null}
              </div>
              <div className="pt-6 pb-12 lg:pb-2">
                <ProgressBar
                  timeline={timeline.sort((a, b) => a.day - b.day)}
                />
              </div>
            </div>
            <div className="w-full lg:w-[260px] shrink-0">
              <div className="w-full h-fit rounded-xl px-4 py-3.5 bg-surface-elevated border border-edge">
                <p className="text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]">
                  Copy Link
                </p>
                <div className="flex items-center justify-between gap-3 mt-2">
                  <h1
                    title={`${initialValues?.data?.paymentLink}`}
                    className="text-[11px] text-blue font-bold cursor-pointer text-nowrap flex-1 overflow-hidden text-ellipsis hover:underline"
                    onClick={(e) => {
                      window.open(
                        `${initialValues?.data?.paymentLink}`,
                        "_blank",
                      );
                    }}
                  >
                    {initialValues?.data?.paymentLink}
                  </h1>
                  <div className="shrink-0" onClick={CopytoClipboard}>
                    <div
                      title="Copy payment link"
                      className="bg-active-background w-8 h-8 rounded-full flex justify-center items-center cursor-pointer border border-edge hover:border-indigo-400/40 transition-colors duration-200"
                    >
                      <img className="w-3 h-4" src={CopyIcon} alt="" />
                    </div>
                  </div>
                </div>
              </div>

              <CustomButton
                className="!w-full !mt-2 !h-[42px]"
                style={{ width: "100%" }}
                variant="primary"
                onClick={HandleTakePayment}
                disabled={loading}
              >
                £ Take Payment
              </CustomButton>
            </div>
          </div>
        </div>

        {/* --------------------------------------------------------- IF SAME CUSTOMER ---------------------------------------------------------------------- */}
        {isSameCustomerChase ? (
          <div className="w-full bg-surface rounded-2xl border border-edge px-5 py-4 mt-4 flex items-center justify-between">
            <h2
              className="text-[13px] font-medium text-text-secondary text-start"
              onClick={() => {
                navigate(
                  `/chaselist?query=${data?.firstName + " " + data?.surname}`,
                );
              }}
            >
              🚨 Another Chase Available for same customer{" "}
              <span className="text-[13px] font-semibold text-blue hover:underline transition-colors text-start text-nowrap cursor-pointer">
                {" "}
                <FontAwesomeIcon
                  icon={faArrowUpRightFromSquare}
                  size="sm"
                  className="mr-1 ml-4"
                />{" "}
                View Details
              </span>
            </h2>
          </div>
        ) : null}

        {/* ------------------------------------------------------- IF CHASE RESTART ------------------------------------------------------------------ */}
        {initialValues?.data?.restartDate ? (
          <div className="w-full bg-surface rounded-2xl border border-edge px-5 py-4 mt-4 flex items-center justify-between">
            <div>
              <h2
                className="text-[13px] font-medium text-text-secondary text-start"
                onClick={() => {
                  navigate(
                    `/chaselist?query=${data?.firstName + " " + data?.surname}`,
                  );
                }}
              >
                🚨 Chase will restart in{" "}
                {moment(initialValues?.data?.restartDate).fromNow()} on{" "}
                <span className="font-semibold text-heading-color tabular-nums">
                  {moment(initialValues?.data?.restartDate).format(
                    "DD-MM-YYYY",
                  )}
                </span>
              </h2>
            </div>
          </div>
        ) : null}

        <div className="w-full bg-surface rounded-2xl border border-edge p-5 sm:p-6 mt-4">
          <div className="w-full">
            <div className="flex items-center justify-between gap-4">
              <p className="flex items-center gap-2 text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]">
                <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                Debt Timeline
              </p>
              <div className="flex items-center gap-4 shrink-0 text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]">
                <span className="w-[80px] sm:w-[96px] text-end">Amount</span>
                <span className="w-[80px] sm:w-[96px] text-end">Collected</span>
                <span className="w-[80px] sm:w-[96px] text-end">
                  Outstanding
                </span>
              </div>
            </div>

            <div className="mt-2">
              {logs?.data?.map((item) => {
                return (
                  <div
                    className="flex items-start sm:items-center justify-between gap-4 py-3.5 border-b border-edge-faint last:border-none"
                    key={item?.id}
                  >
                    <h2 className="w-[86px] sm:w-[100px] text-[12px] font-semibold text-light-gray text-start shrink-0 tabular-nums">
                      {moment(item.createdAt).format("DD/MM/YYYY")}
                    </h2>

                    {/* -------------------------------------------------------- [ IF PAYMENT PLAN SUCCESSEDED OR FAILED  ] ----------------------------------------------- */}
                    {item.type === "payment-plan-collected" ||
                    item.type === "payment-plan-failed-reattempting" ? (
                      <h2 className="flex-1 min-w-0 text-[13px] sm:text-[14px] font-medium text-heading-color text-start capitalize break-words">
                        {/* ---------------------------------------------------------- IF FULL PLAN --------------------------------------------------------- */}
                        {item?.paymentPlan === "full-payment-plan"
                          ? "Full Payment Collected"
                          : item.type.replaceAll("-", " ")}{" "}
                        {/* ---------------------------------------------------------- IF PLAN FAILED --------------------------------------------------------- */}
                        {item?.type === "payment-plan-failed-reattempting" ? (
                          <>{item.attempt}</>
                        ) : null}{" "}
                        {/* ---------------------------------------------------------- IF PLAN IS ACTIVE --------------------------------------------------------- */}
                        {item?.paymentPlan !== "full-payment-plan" &&
                        item?.type !== "payment-plan-failed-reattempting" ? (
                          <>
                            {item.attempt} /
                            {Number(item.paymentPlan?.split("-")[0]) || 1}
                          </>
                        ) : null}{" "}
                        {item?.creator?.name
                          ? `(${item?.creator?.name})`
                          : null}
                      </h2>
                    ) : // ------------------------------------------------------ IF PLAN CANCELLED ----------------------------------------------
                    item?.type === "payment-plan-cancelled" ? (
                      <h2 className="flex-1 min-w-0 text-[13px] sm:text-[14px] font-medium text-heading-color text-start capitalize break-words">
                        {`${
                          item?.paymentPlan?.split("-")[0]
                        } Month Plan Cancelled`}
                        {item?.creator?.name
                          ? ` (${item?.creator?.name})`
                          : null}
                      </h2>
                    ) : item?.type === "debt-collectors-plan-collected" ? (
                      <h2 className="flex-1 min-w-0 text-[13px] sm:text-[14px] font-medium text-heading-color text-start capitalize break-words">
                        {item.type.replaceAll("-", " ")} {item?.attempt} /{" "}
                        {item?.icbPaymentPlan
                          .replaceAll("-", " ")
                          .replace("plan", "")}
                        {item?.creator?.name
                          ? ` (${item?.creator?.name})`
                          : null}
                      </h2>
                    ) : (
                      <h2 className="flex-1 min-w-0 text-[13px] sm:text-[14px] font-medium text-heading-color text-start capitalize break-words">
                        {item.type
                          .replaceAll("-", " ")
                          .replaceAll("sbv1", "SBV1")}
                        {item?.creator?.name
                          ? ` (${item?.creator?.name})`
                          : null}
                      </h2>
                    )}

                    {/* -------------------------------------------------------- [ IF CHASE CREATED OR UPDATED  ] ----------------------------------------------- */}

                    <div className="flex items-start sm:items-center gap-4 shrink-0">
                      <span className="w-[80px] sm:w-[96px] text-[13px] sm:text-[14px] font-semibold text-light-gray text-end shrink-0 tabular-nums">
                        £{item.totalAmount?.toFixed(2) ?? " -"}
                      </span>
                      <span className="w-[80px] sm:w-[96px] text-[13px] sm:text-[14px] font-semibold text-emerald-600 dark:text-emerald-300 text-end shrink-0 tabular-nums">
                        £{item.collectedAmount?.toFixed(2) ?? " -"}
                      </span>
                      <span className="w-[80px] sm:w-[96px] text-[13px] sm:text-[14px] font-semibold text-heading-color text-end shrink-0 tabular-nums">
                        £{item.outstandingBalance?.toFixed(2) ?? " -"}
                      </span>
                    </div>
                  </div>
                );
              })}

              {logsData.length >= 10 && page <= logs.totalCount ? (
                <div className="w-full flex items-center justify-end pt-3">
                  <button
                    className="flex items-center gap-2 text-[13px] font-semibold text-light-gray hover:text-text-primary transition-colors cursor-pointer"
                    onClick={loadMoreLogs}
                    disabled={isFetching}
                  >
                    <FontAwesomeIcon
                      icon={faRotateRight}
                      className="text-[12px]"
                      spin={isFetching}
                    />
                    Load More
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </td>
  );
};

export default ChaseDropMenu;
